'use client';

import React from 'react';

export interface IconButtonProps {
  size?: 'sm' | 'md' | 'lg';
  /** Off-image variants. Ignored when `onImage` is set. */
  variant?: 'ghost' | 'surface' | 'filled';
  /** Frosted high-contrast pill for controls floating over hero imagery. */
  onImage?: boolean;
  disabled?: boolean;
  /** Required accessible label (icon-only control). */
  ariaLabel: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * Square icon-only button (nav, share, close, overflow).
 * Use `onImage` for high-contrast controls floating over hero imagery.
 */
export function IconButton({
  size = 'md',
  variant = 'ghost',
  onImage = false,
  disabled = false,
  ariaLabel,
  onClick,
  children,
  style = {},
  ...rest
}: IconButtonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof IconButtonProps | 'aria-label'>) {
  const dims = { sm: 36, md: 44, lg: 48 };

  let palette: React.CSSProperties;
  if (onImage) {
    palette = {
      background: 'rgba(31,27,22,0.42)',
      color: '#FFFFFF',
      border: '1px solid rgba(255,255,255,0.18)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    };
  } else {
    palette = {
      ghost: {
        background: 'transparent',
        color: 'var(--text-secondary)',
        border: '1px solid transparent',
      },
      surface: {
        background: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-subtle)',
      },
      filled: {
        background: 'var(--brand-wash)',
        color: 'var(--brand)',
        border: '1px solid transparent',
      },
    }[variant];
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: dims[size],
        height: dims[size],
        borderRadius: 'var(--r-full)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'background var(--dur-micro) var(--ease-standard)',
        ...palette,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}
